import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface MarqueeProps {
    children: ReactNode
    speed?: number
    direction?: 'left' | 'right'
    hoverFactor?: number
    className?: string
    trackClassName?: string
    copies?: number
}

/**
 * Endless horizontal drift. Speed eases towards `speed * hoverFactor` while
 * hovered (0 pauses it), and the loop only runs while it is on screen.
 * Each copy should end with the same spacing it uses between items.
 */
export const Marquee: FC<MarqueeProps> = ({
    children,
    speed = 40,
    direction = 'left',
    hoverFactor = 0.3,
    className,
    trackClassName,
    copies = 3
}): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const copyRef = useRef<HTMLDivElement>(null)
    const hovering = useRef(false)
    const reduced = useReducedMotion()

    useEffect(() => {
        const container = containerRef.current
        const track = trackRef.current
        const copy = copyRef.current

        if (!container || !track || !copy) return

        if (reduced) {
            track.style.transform = ''
            return
        }

        let width = copy.offsetWidth
        let offset = direction === 'left' ? 0 : -width
        let current = speed
        let last = performance.now()
        let frame = 0
        let visible = false

        const tick = (now: number) => {
            frame = 0

            if (!visible) return

            const delta = Math.min(0.05, (now - last) / 1000)
            const target = hovering.current ? speed * hoverFactor : speed

            last = now
            current += (target - current) * Math.min(1, delta * 4)

            if (width > 0) {
                if (direction === 'left') {
                    offset -= current * delta
                    if (offset <= -width) offset += width
                }
                else {
                    offset += current * delta
                    if (offset >= 0) offset -= width
                }

                track.style.transform = `translate3d(${offset.toFixed(2)}px, 0, 0)`
            }

            frame = requestAnimationFrame(tick)
        }

        const resize = new ResizeObserver(() => {
            width = copy.offsetWidth
        })

        const observer = new IntersectionObserver(([entry]) => {
            visible = entry.isIntersecting

            if (visible && !frame) {
                last = performance.now()
                frame = requestAnimationFrame(tick)
            }
        }, { rootMargin: '120px' })

        resize.observe(copy)
        observer.observe(container)

        return () => {
            cancelAnimationFrame(frame)
            resize.disconnect()
            observer.disconnect()
        }
    }, [speed, direction, hoverFactor, reduced])

    return (
        <div
            ref={containerRef}
            className={cn('overflow-hidden', className)}
            onMouseEnter={() => { hovering.current = true }}
            onMouseLeave={() => { hovering.current = false }}
        >
            <div ref={trackRef} className={cn('flex', reduced ? 'w-full flex-wrap justify-center' : 'w-max')}>
                {Array.from({ length: reduced ? 1 : copies }, (_, index) => (
                    <div
                        key={index}
                        ref={index === 0 ? copyRef : undefined}
                        aria-hidden={index > 0 ? true : undefined}
                        className={cn('flex shrink-0 items-center', reduced && 'flex-wrap justify-center', trackClassName)}
                    >
                        {children}
                    </div>
                ))}
            </div>
        </div>
    )
}
